import { useState } from "react";
import moment from "moment";
import { Row, Col, Table, Button, Form, FormGroup, Label, Input } from "reactstrap";
import { calcularRecordatorioRenovacion, leerVencimientoCertificadoCer } from "./fielCertificado";

const rolesAcceso = [
    { value: 'administrador', label: 'Administrador' },
    { value: 'operador', label: 'Operador' },
    { value: 'consulta', label: 'Solo lectura' },
    { value: 'rec', label: 'Representante de cumplimiento (REC)' }
];

const usuarioInicial = () => {
    const correoSesion = localStorage.getItem('psaAgent');
    if (!correoSesion) {
        return [];
    }
    return [{
        id: 'usuario-sesion',
        nombre: '',
        correo: correoSesion,
        rol: 'administrador',
        activo: true
    }];
};

const initialNuevoUsuario = {
    nombre: '',
    correo: '',
    rol: 'operador',
    activo: true,
    fielCertificado: null,
    fielLlave: null,
    fielVencimiento: null,
    fielRecordatorioRenovacion: null
};

function crearNuevoUsuarioVacio() {
    return { ...initialNuevoUsuario };
}

function esArchivoFielValido(archivo, extension) {
    return archivo && archivo.name.toLowerCase().endsWith(extension);
}

function etiquetaRol(valor) {
    return rolesAcceso.find((rol) => rol.value === valor)?.label || valor;
}

export default function UsuariosAccesos() {
    const [usuarios, setUsuarios] = useState(usuarioInicial);
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [nuevoUsuario, setNuevoUsuario] = useState(initialNuevoUsuario);
    const [errorFormulario, setErrorFormulario] = useState('');

    function handleNuevoUsuarioChange(name) {
        return function (e) {
            const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
            const next = { ...nuevoUsuario, [name]: value };
            if (name === 'rol' && value !== 'rec') {
                next.fielCertificado = null;
                next.fielLlave = null;
                next.fielVencimiento = null;
                next.fielRecordatorioRenovacion = null;
            }
            setNuevoUsuario(next);
            setErrorFormulario('');
        };
    }

    function handleFielChange(campo, extensionEsperada) {
        return async function (e) {
            const archivo = e.target.files && e.target.files[0];
            if (!archivo) {
                setNuevoUsuario({
                    ...nuevoUsuario,
                    [campo]: null,
                    ...(campo === 'fielCertificado' ? {
                        fielVencimiento: null,
                        fielRecordatorioRenovacion: null
                    } : {})
                });
                return;
            }
            if (!esArchivoFielValido(archivo, extensionEsperada)) {
                setErrorFormulario(`Debes adjuntar un archivo ${extensionEsperada}`);
                e.target.value = '';
                return;
            }

            if (campo === 'fielCertificado') {
                let vencimiento = null;
                let recordatorio = null;
                try {
                    vencimiento = await leerVencimientoCertificadoCer(archivo);
                    if (vencimiento) {
                        recordatorio = calcularRecordatorioRenovacion(vencimiento);
                    }
                } catch (err) {
                    console.log(err);
                }
                setNuevoUsuario({
                    ...nuevoUsuario,
                    fielCertificado: archivo,
                    fielVencimiento: vencimiento ? vencimiento.toISOString() : null,
                    fielRecordatorioRenovacion: recordatorio ? recordatorio.toISOString() : null
                });
            } else {
                setNuevoUsuario({ ...nuevoUsuario, [campo]: archivo });
            }
            setErrorFormulario('');
        };
    }

    function handleAgregarUsuario(e) {
        e.preventDefault();
        const nombre = nuevoUsuario.nombre.trim();
        const correo = nuevoUsuario.correo.trim().toLowerCase();

        if (!nombre) {
            setErrorFormulario('Indica el nombre del usuario.');
            return;
        }
        if (!correo) {
            setErrorFormulario('Indica el correo electrónico.');
            return;
        }
        if (usuarios.some((u) => u.correo.toLowerCase() === correo)) {
            setErrorFormulario('Ya existe un usuario con ese correo.');
            return;
        }

        if (nuevoUsuario.rol === 'rec') {
            if (!esArchivoFielValido(nuevoUsuario.fielCertificado, '.cer')) {
                setErrorFormulario('El REC debe adjuntar el certificado FIEL (.cer).');
                return;
            }
            if (!esArchivoFielValido(nuevoUsuario.fielLlave, '.key')) {
                setErrorFormulario('El REC debe adjuntar la llave privada FIEL (.key).');
                return;
            }
        }

        setUsuarios([
            ...usuarios,
            {
                id: `usr-${Date.now()}`,
                nombre,
                correo,
                rol: nuevoUsuario.rol,
                activo: nuevoUsuario.activo,
                fiel: nuevoUsuario.rol === 'rec' ? {
                    certificado: nuevoUsuario.fielCertificado.name,
                    llave: nuevoUsuario.fielLlave.name,
                    vencimiento: nuevoUsuario.fielVencimiento,
                    recordatorioRenovacion: nuevoUsuario.fielRecordatorioRenovacion
                } : null
            }
        ]);
        setNuevoUsuario(crearNuevoUsuarioVacio());
        setMostrarFormulario(false);
        setErrorFormulario('');
    }

    function handleEliminarUsuario(id) {
        setUsuarios(usuarios.filter((usuario) => usuario.id !== id));
    }

    return (
        <div>
            <h3>Usuarios y accesos</h3>
            <Row>
                <Col>
                    <p>Aquí podrás gestionar los usuarios y accesos a tu sistema.</p>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col className="text-end">
                    <Button
                        color="secondary"
                        onClick={() => {
                            if (mostrarFormulario) {
                                setNuevoUsuario(crearNuevoUsuarioVacio());
                                setErrorFormulario('');
                            }
                            setMostrarFormulario(!mostrarFormulario);
                        }}
                    >
                        {mostrarFormulario ? 'Cancelar' : 'Agregar usuario'}
                    </Button>
                </Col>
            </Row>

            {mostrarFormulario && (
                <Row className="mb-4">
                    <Col lg={6}>
                        <Form onSubmit={handleAgregarUsuario}>
                            <h5 className="mb-3">Nuevo usuario</h5>
                            {errorFormulario && (
                                <p className="text-danger small">{errorFormulario}</p>
                            )}
                            <FormGroup>
                                <Label for="nombreUsuario">Nombre *</Label>
                                <Input
                                    id="nombreUsuario"
                                    className="FormInput"
                                    value={nuevoUsuario.nombre}
                                    onChange={handleNuevoUsuarioChange('nombre')}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="correoUsuario">Correo electrónico *</Label>
                                <Input
                                    type="email"
                                    id="correoUsuario"
                                    className="FormInput"
                                    value={nuevoUsuario.correo}
                                    onChange={handleNuevoUsuarioChange('correo')}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="rolUsuario">Rol de acceso</Label>
                                <Input
                                    type="select"
                                    id="rolUsuario"
                                    className="FormInput"
                                    value={nuevoUsuario.rol}
                                    onChange={handleNuevoUsuarioChange('rol')}
                                >
                                    {rolesAcceso.map((rol) => (
                                        <option key={rol.value} value={rol.value}>{rol.label}</option>
                                    ))}
                                </Input>
                            </FormGroup>

                            {nuevoUsuario.rol === 'rec' && (
                                <>
                                    <p className="text-muted small">
                                        Como Representante de cumplimiento (REC), debes adjuntar tu e.firma (FIEL): certificado y llave privada.
                                    </p>
                                    <FormGroup>
                                        <Label for="fielCertificado">Certificado e.firma (.cer) *</Label>
                                        <Input
                                            type="file"
                                            id="fielCertificado"
                                            accept=".cer"
                                            onChange={handleFielChange('fielCertificado', '.cer')}
                                        />
                                        {nuevoUsuario.fielCertificado && (
                                            <small className="text-success d-block mt-1">
                                                {nuevoUsuario.fielCertificado.name}
                                            </small>
                                        )}
                                        {nuevoUsuario.fielCertificado && nuevoUsuario.fielVencimiento && (
                                            <div className="small mt-2">
                                                <p className="mb-1">
                                                    <strong>Caducidad:</strong>{' '}
                                                    {moment(nuevoUsuario.fielVencimiento).format('DD/MM/YYYY')}
                                                </p>
                                                <p className="text-muted mb-0">
                                                    Se te recordará renovar tu certificado el día:{' '}
                                                    {moment(nuevoUsuario.fielRecordatorioRenovacion).format('DD/MM/YYYY')}
                                                </p>
                                            </div>
                                        )}
                                        {nuevoUsuario.fielCertificado && !nuevoUsuario.fielVencimiento && (
                                            <small className="text-warning d-block mt-1">
                                                No se pudo leer la caducidad del certificado.
                                            </small>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="fielLlave">Llave privada e.firma (.key) *</Label>
                                        <Input
                                            type="file"
                                            id="fielLlave"
                                            accept=".key"
                                            onChange={handleFielChange('fielLlave', '.key')}
                                        />
                                        {nuevoUsuario.fielLlave && (
                                            <small className="text-success d-block mt-1">
                                                {nuevoUsuario.fielLlave.name}
                                            </small>
                                        )}
                                    </FormGroup>
                                </>
                            )}

                            <FormGroup check className="mb-3">
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={nuevoUsuario.activo}
                                        onChange={handleNuevoUsuarioChange('activo')}
                                    />
                                    {' '}Usuario activo
                                </Label>
                            </FormGroup>
                            <Button color="secondary" type="submit">Guardar usuario</Button>
                        </Form>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    <Table responsive>
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Correo</th>
                                <th>Rol</th>
                                <th>FIEL</th>
                                <th>Activo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuarios.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-muted text-center">
                                        No hay usuarios registrados. Agrega el primero con el botón superior.
                                    </td>
                                </tr>
                            )}
                            {usuarios.map((usuario) => (
                                <tr key={usuario.id}>
                                    <td>{usuario.nombre || '—'}</td>
                                    <td>{usuario.correo}</td>
                                    <td>{etiquetaRol(usuario.rol)}</td>
                                    <td>
                                        {usuario.rol === 'rec' ? (
                                            usuario.fiel?.certificado && usuario.fiel?.llave ? (
                                                <span className="small" title={`${usuario.fiel.certificado} / ${usuario.fiel.llave}`}>
                                                    Adjuntada
                                                </span>
                                            ) : (
                                                <span className="text-warning small">Pendiente</span>
                                            )
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td>{usuario.activo ? 'Sí' : 'No'}</td>
                                    <td>
                                        <Button
                                            color="link"
                                            className="p-0 text-danger"
                                            onClick={() => handleEliminarUsuario(usuario.id)}
                                        >
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </div>
    );
}
